import React from 'react';
import { GenericPage } from './GenericPage';
import { FunnelGraphic, AttackPathGraphic, WorkflowRibbon, IntegrationWall } from '../components/Graphics';
export function ExposureValidation() {
  return <GenericPage hero={{
    eyebrow: 'Picus Platform',
    headline: 'Know what actually matters',
    subhead: 'Discovery tells you what exists. Exposure Validation tells you what matters. Picus uses real payloads, real environment context, and actual control performance to help teams separate theoretical risk from evidence-backed priority.',
    primaryCta: {
      label: 'Get a Demo',
      to: '/contact'
    },
    secondaryCta: {
      label: 'Download Datasheet',
      to: '/contact'
    },
    graphic: <FunnelGraphic data-id="element-332" />
  }} modules={[{
    title: 'Scanner output is not a prioritization strategy',
    subtitle: 'Security teams do not need more long lists. They need evidence that shows which weaknesses actually matter in this environment and which ones can safely wait.',
    type: 'text',
    graphic: 'Huge scanner list collapsing into a short validated queue.'
  }, {
    title: 'Autonomous exposure validation starts here',
    subtitle: 'Exposure Validation is where the platform begins to separate generic testing from useful, environment-aware validation. It ties real payloads to real context so the output means something operationally.',
    type: 'text',
    graphic: 'Generic scanner on left versus context-rich Picus validated exposure model on right.'
  }, {
    title: 'Real payloads. Real context. Better priorities',
    subtitle: 'Picus validates exposures in the context of real assets, controls, attack paths, and environment conditions. That helps teams move from theory to action.',
    type: 'text',
    graphic: 'Validated exposure card surrounded by asset, control, path, and threat context.'
  }, {
    title: 'Prioritize exposures critical to your environment',
    subtitle: 'Picus Exposure Validation is built to validate whether an exposure is theoretical or exploitable. The point is not more scoring. The point is remediation effort focused on real risk.',
    type: 'text',
    graphic: 'Theoretical versus exploitable split with one highlighted path.'
  }, {
    title: 'Calculate your real risk',
    subtitle: 'Picus Score combines control performance, asset importance, business context, vulnerability severity, and exploit likelihood signals. The result is grounded in how your environment actually behaves, not just how a vulnerability is described.',
    type: 'text',
    graphic: 'Score wheel with factors: control performance, asset criticality, severity, exploit likelihood.'
  }, {
    title: 'Powered by the platform',
    subtitle: 'Exposure Validation becomes more useful because it is fed by Security Control Validation, Attack Path Validation, and Attack Surface Validation. BAS, Automated Penetration Testing, and attack surface visibility all improve the quality of the final prioritization.',
    type: 'text',
    graphic: 'Three feeding pillars into one Exposure Validation core.'
  }, {
    title: 'Fix the right things first',
    subtitle: 'Not every weakness deserves the same urgency. Picus helps teams focus on the exposures that are actually dangerous now.',
    type: 'text',
    graphic: 'Three-column compare: theoretical / validated / prioritized.',
    proof_chips: ['Evidence-backed prioritization', 'Real payloads and real context', 'Built for teams drowning in scanner noise']
  }]} data-id="element-331" />;
}
export function AutomatedPenTesting() {
  return <GenericPage hero={{
    eyebrow: 'Picus Platform',
    headline: 'Prove exploitability in depth',
    subhead: 'Picus Automated Penetration Testing helps teams validate real exploitability and identify viable attack paths to meaningful impact. It adds offensive depth where BAS gives defensive breadth.',
    primaryCta: {
      label: 'Get a Demo',
      to: '/contact'
    },
    secondaryCta: {
      label: 'See the Platform',
      to: '/platform'
    },
    graphic: <AttackPathGraphic data-id="element-334" />
  }} modules={[{
    title: 'Not every weakness leads anywhere',
    subtitle: 'Some weaknesses are real but contained. Others can be chained into meaningful attack paths. Picus helps teams understand the difference.',
    type: 'text',
    graphic: 'Two side-by-side path cards: dead end versus meaningful impact.'
  }, {
    title: 'Validate what an attacker can actually do',
    subtitle: 'Picus Automated Penetration Testing helps teams go deeper than broad discovery and prove how an attacker could actually move through the environment.',
    type: 'text',
    graphic: 'Exploitability path animation with 3–4 steps.'
  }, {
    title: 'Attack Path Validation makes the path visible',
    subtitle: 'Attack Path Validation helps teams understand how weaknesses connect into meaningful routes to impact. That turns isolated findings into exploitability stories that can actually be prioritized and fixed.',
    type: 'text',
    graphic: 'Path map with one validated route highlighted.'
  }, {
    title: 'Depth where BAS gives breadth',
    subtitle: 'BAS is strong for validating controls broadly. Automated Penetration Testing is strong for proving exploitability deeply. Picus Platform brings both methods together.',
    type: 'text',
    graphic: 'BAS breadth versus APT depth compare strip.'
  }, {
    title: 'A proven present-day motion inside a broader future platform',
    subtitle: 'Automated Penetration Testing is one of the clearest growth motions in the market today. Picus uses it as both a product buyers already understand and a key pillar inside a broader autonomous exposure validation platform.',
    type: 'text',
    graphic: 'APT card leading into platform diagram.'
  }, {
    title: 'Better together with Exposure Validation',
    subtitle: 'Automated Penetration Testing is strongest when it works alongside control validation and exposure validation. Picus Platform brings those methods together in one system.',
    type: 'text',
    graphic: 'BAS / Exposure Validation / APT compare tiles.'
  }]} data-id="element-333" />;
}
export function PicusSwarm() {
  return <GenericPage hero={{
    eyebrow: 'Picus Platform',
    headline: 'Reduce the manual gap between finding and action',
    subhead: 'Picus Swarm is the workflow layer of Picus Platform. It helps teams trigger validation based on meaningful change, route the right context to the right owner, and reduce the drag between signal, validation, priority, and action.',
    primaryCta: {
      label: 'See Picus Swarm',
      to: '/contact'
    },
    secondaryCta: {
      label: 'Get a Demo',
      to: '/contact'
    },
    graphic: <WorkflowRibbon data-id="element-336" />
  }} modules={[{
    title: 'AI-native where it matters',
    subtitle: 'Every security vendor has AI now. The real question is whether that AI knows your environment well enough to operate usefully inside it. Picus Swarm is grounded in the context window of your actual stack.',
    type: 'text',
    graphic: 'Generic AI output on left versus context-rich Picus Swarm decisions on right.'
  }, {
    title: 'Manual-only workflows do not scale',
    subtitle: 'The issue is not that people are too slow. The issue is that manual handoffs, manual triage, and manual coordination do not scale as threats and environments change faster.',
    type: 'text',
    graphic: 'Manual handoff chain on left versus streamlined Swarm lane on right.'
  }, {
    title: 'Less administrative drag, more validated action',
    subtitle: 'Swarm helps teams spend less time moving information around and more time acting on the right problems. It is not AI for show. It is workflow for validated security operations.',
    type: 'text',
    graphic: 'Before / after operating model strip.'
  }, {
    title: 'Built for tunable autonomy',
    subtitle: 'Some teams want recommendations. Others want workflow assistance. Over time, Swarm helps teams move toward a more scalable operating model without losing control.',
    type: 'text',
    graphic: 'Manual ↔ assisted ↔ autonomous continuum with guardrail icons.'
  }, {
    title: 'Triggered by meaningful change',
    subtitle: 'Swarm becomes useful when signals matter: a new threat, a new asset, a new policy, a drifted control, or a new exposure path. Those changes can trigger validation and route the next step automatically.',
    type: 'text',
    graphic: 'Signal trigger tiles feeding into validation.'
  }, {
    title: 'Autonomy with chain of custody',
    subtitle: 'Picus Swarm is designed to be transparent and governable. The value is not just autonomy. It is traceable, auditable autonomy inside guardrails you control.',
    type: 'text',
    graphic: 'Traceability log or chain-of-custody visual.'
  }]} data-id="element-335" />;
}
export function Integrations() {
  return <GenericPage hero={{
    eyebrow: 'Picus Platform',
    headline: 'Context starts with your stack',
    subhead: 'Picus Platform becomes context-rich by ingesting signals across EDR, SIEM, firewalls, cloud, identity, vulnerability, and workflow systems. Those integrations make autonomous exposure validation useful in your actual environment, not a generic model.',
    primaryCta: {
      label: 'See Supported Technologies',
      to: '/integrations'
    },
    secondaryCta: {
      label: 'Request an Integration',
      to: '/contact'
    },
    graphic: <IntegrationWall data-id="element-338" />
  }} modules={[{
    title: 'Works with the tools you already own',
    subtitle: 'Picus does not ask you to rebuild your stack. It helps you validate the one you already have.',
    type: 'text',
    graphic: 'Simple integration orbit with key categories.'
  }, {
    title: 'Your stack has the signals',
    subtitle: 'Threats, controls, policies, detections, assets, identities, and workflows already produce the raw inputs. Picus Platform turns those signals into validated action.',
    type: 'text',
    graphic: 'Many signals feeding one validation core.'
  }, {
    title: 'Broad integrations. Better validation',
    subtitle: 'Picus integrates with major controls and systems so teams can validate what works, what changed, and what matters in practice.',
    type: 'text',
    graphic: 'Category wall with SIEM, EDR/XDR, firewall, cloud, identity, workflow.'
  }, {
    title: 'Make the stack work as a system',
    subtitle: 'The stack is already there. The challenge is proving whether it works together. Picus helps answer that question.',
    type: 'text',
    graphic: 'Stack layers joined by one validation spine.'
  }]} data-id="element-337" />;
}